import { action, observable } from "mobx";
import { FormulaFormVM } from "../template/FormulaFormVM";
import { HydraulicFormulaController } from "cd-eft/services/eftSetApi";
import MessageCenter from "cd-eft/components/common/MessageCenter/MessageCenterVM";
import { StraightDropInletRequestDto, StraightDropResponseDto } from "cd-eft/_codegen_/openapi-generator/eft-set";

interface IStraightDropForm {
  weirLength: string,
  headAboveWeir: string,
  weirElevation: string,
  sillElevation: string,
}
export class StraightDropVM extends FormulaFormVM {
  readonly formLabelsForExport;
  readonly resultLabelsForExport;
  constructor(props) {
    super(props);
    // Any Form specific contructor code. Otherwise, remove contructor - use parent.
    this.formLabelsForExport = {
      weirLength: 'Weir Length (ft)',
      headAboveWeir: 'Head Above Weir (ft)',
      weirElevation: 'Weir Elevation (ft)',
      sillElevation: 'Sill Elevation (ft)',
    };
    this.resultLabelsForExport = {
      flowQ: 'Flow Q (cfs)',
    }
    this.disablePrintBtn = true;
  }
  // Expand with onChange handlers, onBlur validation, Submit handler, on submit validation, individual variables, etc.
  @observable straightDropForm: IStraightDropForm = {
    weirLength: "0.00",
    headAboveWeir:"0.00",
    weirElevation: "0.00",
    sillElevation: "0.00",
  }
  @observable formValidation = {
    hasError: true,
    weirLength: false,
    headAboveWeir: false,
    sillElevation: false,
    weirElevation: false,
    formErrors: {
      weirLength: '',
      weirElevation: '',
      headAboveWeir: '',
      sillElevation: ''
    },
  };
  // To properly override the calculationResults property in the StraightDropInletVM class with the specific type IStraightDropResults
  @observable straightDropFormInputSnapshot: IStraightDropForm | undefined = undefined;

  @action.bound handleOnKeyDown(event) {
    ['e', 'E', '+', '-'].includes(event.key) && event.preventDefault();
  }

  @action.bound StraightDropErrorClass(field) {
    const errorMessage = this.formValidation.formErrors[field];
    return errorMessage?.length === 0 ? '' : ' usa-input--error';
  }
  @action.bound setFixedValue(name, fixed) {
    const event1 = {
     target: {
     name: name,
     value: this.roundingHelperString(fixed, 2)
     }
     }
    this.handleInputChange(event1, true);
     }
  @action.bound handleInputChange(event, within? : boolean) {
    const { name } = event.target;
    let value = event.target.value;
    if (!within && value.length > 7) {
      return;
    }
    switch (name) {
      case 'weir-length':
        this.straightDropForm.weirLength = value;
        break;
      case 'weir-elevation':
        this.straightDropForm.weirElevation = value;
        break;
      case 'head-above-weir':
        this.straightDropForm.headAboveWeir = value;
        break;
      case 'sill-elevation':
        this.straightDropForm.sillElevation = value;
        break;
    }
  }

  @action.bound async handleFormChange(event) {
    const { name, value } = event.target;
    if (value.length > 7) {
      return
    }
    switch (name) {
      case 'weir-length':
        this.straightDropForm.weirLength = value;
        break;
      case 'weir-elevation':
        this.straightDropForm.weirElevation = value;
        break;
      case 'head-above-weir':
        this.straightDropForm.headAboveWeir = value;
        break;
      case 'sill-elevation':
        this.straightDropForm.sillElevation = value;
        break;
    }
  }
  @action.bound handleInputOnBlur(name, value) {
    this.validateInput(name, value);
  }
  @action.bound async validateInput(name, value) {
    let val = 0;
    if (value !== '' && value !== undefined) {
      val = this.roundingHelperNumber(parseFloat(value), 2);
      this.setFixedValue(name, val);
    }

    switch (name) {
      case 'weir-length':
        if (value === '' || Number.isNaN(value) || value === 0 || val < 0.01) {
          this.formValidation.weirLength = true;
          this.formValidation.formErrors.weirLength = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', undefined, this.formLabelsForExport.weirLength.replace(/(\s\([\w\W]*\))/, ''));
        }
        else {
          this.formValidation.weirLength = false;
          this.formValidation.formErrors.weirLength = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'head-above-weir':
        if (value === '' || Number.isNaN(value) || value === 0 || val < 0.01) {
          this.formValidation.headAboveWeir = true;
          this.formValidation.formErrors.headAboveWeir = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', undefined, this.formLabelsForExport.headAboveWeir.replace(/(\s\([\w\W]*\))/, ''));
        }
        else {
          this.formValidation.headAboveWeir = false;
          this.formValidation.formErrors.headAboveWeir = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'weir-elevation':
        if (value === '' || Number.isNaN(value) || value === 0 || val < 0.01) {
          this.formValidation.weirElevation = true;
          this.formValidation.formErrors.weirElevation = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', undefined, this.formLabelsForExport.weirElevation.replace(/(\s\([\w\W]*\))/, ''))
        }
        else {
          this.formValidation.weirElevation = false;
          this.formValidation.formErrors.weirElevation = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'sill-elevation':
        if (value < 0) {
          this.formValidation.sillElevation = true;
          this.formValidation.formErrors.sillElevation = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_14', undefined, this.formLabelsForExport.sillElevation)
        }
        else {
          this.formValidation.sillElevation = false;
          this.formValidation.formErrors.sillElevation = '';
          this.formValidation.hasError = false;
        }
        break;
    }
  }
  get validateRequiredInputs() {
    if (!this.formValidation.weirLength && !this.formValidation.headAboveWeir &&
      !this.formValidation.sillElevation && !this.formValidation.weirElevation) {
      return true
    }
    else {
      return false
    }
  }
  async validateReqInput() {
    await this.validateInput("weir-length", this.straightDropForm.weirLength);
    await this.validateInput("head-above-weir", this.straightDropForm.headAboveWeir);
    await this.validateInput("weir-elevation", this.straightDropForm.weirElevation);
    await this.validateInput("sill-elevation", this.straightDropForm.sillElevation);
    // await this.validateElevationComparison();// Commented until we know we want this in
    // Message is currently an INFO type as well.
  }

  @action.bound async computefnStraightDrop() {
    this.disableComputeBtn = true;
    this.disablePrintBtn = true;
    this.isLoading = true;
    MessageCenter.blockCalls(['Common_26']);
    await this.validateReqInput();
    if (this.validateRequiredInputs) {
      try {
        const requestPayload: StraightDropInletRequestDto = {
          weirLength: parseFloat(this.straightDropForm.weirLength),
          headAboveWeir: parseFloat(this.straightDropForm.headAboveWeir),
          weirElevation: parseFloat(this.straightDropForm.weirElevation) ? parseFloat(this.straightDropForm.weirElevation) : 0,
          sillElevation: parseFloat(this.straightDropForm.sillElevation) ? parseFloat(this.straightDropForm.sillElevation) : 0,
        }
        const response = await HydraulicFormulaController.calculateStraightDropInletResultsUsingPOST(requestPayload)
        const shortCode = response?.data?.errorResponse?.errorCode?.replace(/[123]_[a-zA-Z]+_[a-zA-Z]+_/, '');
        if (response && response.data) {
          const responsePayload: StraightDropResponseDto = response.data;
          if (this.hasErrorContent(responsePayload.errorResponse)) {
            MessageCenter.resetMessageCenter();
            MessageCenter.addNotification(responsePayload.errorResponse!.errorMessage!, responsePayload.errorResponse!.errorType!, responsePayload.errorResponse!.errorCode)
            // if (responsePayload.errorResponse!.errorCode?.includes('Formula_22')) {
            //   this.validateElevationComparison();
            // }
            this.hasResults = false;
            this.disablePrintBtn = true;
            this.calculationResults = undefined;
            this.resetPlotData();
          }
          else if (response.data.validationPass) {
            this.disablePrintBtn = false;
            MessageCenter.resetMessageCenter();
            this.straightDropFormInputSnapshot = { ...this.straightDropForm }
            this.calculationResults = {
              flowQ: responsePayload.flowQ ? responsePayload.flowQ : 0,
            };
            this.setPlotData(responsePayload.stageDischargeList!);
            this.hasResults = true;
          }
        }
      } catch (error) {
        console.error(error)
      }
      finally {
        this.disableComputeBtn = false;
        this.isLoading = false;
      }
    }
    else {
      // If we are missing required inputs or they are invalid
      // If field validation has been added, input specific messages should not be shown at this time.
      // MessageCenter.resetMessageCenter();
      await MessageCenter.addNotificationFromCode('Common_26');
      this.hasResults = false;
      this.disablePrintBtn = true;
      this.resetPlotData();
    }
    this.disableComputeBtn = false;
    this.isLoading = false;
    MessageCenter.unblockCalls();
  }

  @action.bound launchAndExport() {
    // disable print button
    this.disablePrintBtn = true;
    this.exportedInputs = [];
    this.exportedResults = [];
    for (const input in this.straightDropFormInputSnapshot) {
      const label = this.formLabelsForExport[input]
      const value = this.straightDropFormInputSnapshot[input]
      if (label && value !== '') {
        this.exportedInputs.push({
          label: this.formLabelsForExport[input],
          value: this.straightDropFormInputSnapshot[input],
        });
      }
    }
    for (const result in this.calculationResults) {
      if (result !== 'errorResponse' || 'validationPass') {
        const label = this.resultLabelsForExport[result];
        const value = this.calculationResults[result];
        if (label && value !== undefined) {
          this.exportedResults.push({
            label: this.resultLabelsForExport[result],
            value: this.calculationResults[result] && !isNaN(this.calculationResults[result]) ? this.roundingHelperString(this.calculationResults[result], 2) : 'N/A',
          });
        }
      }
    }
    this.printFormula();
    this.disablePrintBtn = false;
  }

  @action.bound initializeDefaultValues(): void {
    this.straightDropForm = {
      weirLength: this.roundingHelperString(this.defaultValues.weirLength, 2),
      headAboveWeir: this.roundingHelperString(this.defaultValues.headAboveWeir, 2),
      weirElevation: this.roundingHelperString(this.defaultValues.weirElevation, 2),
      sillElevation: this.roundingHelperString(this.defaultValues.sillElevation, 2),
    }
  }
}